<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { RefreshCw } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Skeleton } from "@/components/ui/skeleton";
  import { Spinner } from "@/components/ui/spinner";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import type { SurveyContext } from "@/types";
  import { useSurveyContextStore } from "@stores/survey/surveyContext";
  import ContextRichEditor from "./ContextRichEditor.vue";

  const props = defineProps<{ context: SurveyContext }>();
  const emit = defineEmits<{ generate: [] }>();

  const COVERED_TOPICS = ["Objectives", "Target", "Brands and competitors", "Hypotheses", "Previous results"];

  const { draft, saving, hasUnsavedChanges: dirty } = storeToRefs(useSurveyContextStore());
  const writing = ref(false);
  const editorKey = ref(0);
  const confirmOpen = ref(false);

  const content = computed(() => props.context.content?.trim() ?? "");
  const generating = computed(() => props.context.status === "generating");
  const showEditor = computed(() => !generating.value && (content.value !== "" || writing.value));
  const footer = computed(() => {
    if (props.context.status === "failed") return "The last generation failed: the text above is the previous context.";
    if (!props.context.generated_at) return null;

    const generatedAt = new Date(props.context.generated_at);
    const date = generatedAt.toLocaleDateString("en-US", { dateStyle: "medium" });
    const modified =
      props.context.updated_at && new Date(props.context.updated_at).getTime() - generatedAt.getTime() > 5000;
    return `Generated by Episto AI from the questionnaire and the sources · ${date}${modified ? " (Modified)" : ""}`;
  });

  const discard = () => {
    draft.value = null;
    editorKey.value += 1;
  };

  watch(
    () => props.context.content,
    () => {
      draft.value = null;
    },
  );
  watch(generating, (isGenerating) => {
    if (isGenerating) draft.value = null;
  });

  const requestGeneration = () => {
    if (content.value || dirty.value) confirmOpen.value = true;
    else emit("generate");
  };
</script>

<template>
  <section
    aria-label="Survey context"
    class="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border">
    <div class="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
      <h2 class="heading-title">Survey context</h2>
      <div
        v-if="showEditor || generating"
        class="flex flex-wrap justify-end gap-2">
        <Button
          v-if="dirty"
          variant="ghost"
          :disabled="saving"
          @click="discard">
          Discard
        </Button>
        <Button
          variant="outline"
          :disabled="generating || saving"
          @click="requestGeneration">
          <Spinner v-if="generating" />
          <RefreshCw v-else />
          Regenerate with AI
        </Button>
      </div>
    </div>

    <div
      v-if="generating"
      class="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-7 py-6">
      <div class="flex items-center gap-2 text-13 text-muted-foreground">
        <AiStar class="size-4 text-primary" />
        Episto AI is reading the questionnaire and your sources…
      </div>
      <div
        v-for="index in 4"
        :key="index"
        class="flex flex-col gap-2">
        <Skeleton class="h-3.5 w-32" />
        <Skeleton class="h-3 w-full" />
        <Skeleton class="h-3 w-4/5" />
      </div>
    </div>

    <ContextRichEditor
      v-else-if="showEditor"
      :key="editorKey"
      :markdown="context.content ?? ''"
      :disabled="saving"
      @change="draft = $event" />

    <div
      v-else
      class="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 overflow-y-auto px-6 py-8 text-center">
      <span class="flex size-13 items-center justify-center rounded-full bg-muted text-primary">
        <AiStar class="size-6" />
      </span>
      <div class="flex max-w-xl flex-col gap-2">
        <h3 class="heading-title">Give Episto AI the context of your study</h3>
        <p class="text-sm leading-relaxed text-muted-foreground">
          Add the brief, kick-off notes or previous results. Episto AI turns them into a context that AI Insights and
          the questionnaire assistant use to answer with your objectives in mind.
        </p>
      </div>
      <div class="flex flex-wrap justify-center gap-2">
        <Button
          size="lg"
          @click="emit('generate')">
          <AiStar />
          {{ context.sources.length ? "Generate with AI" : "Generate from the questionnaire only" }}
        </Button>
        <Button
          variant="ghost"
          size="lg"
          @click="writing = true">
          Write it myself
        </Button>
      </div>
      <div class="flex w-full max-w-xl flex-col items-center gap-2.5 border-t pt-5">
        <span class="text-xs font-medium text-muted-foreground">A good context covers</span>
        <div class="flex flex-wrap justify-center gap-1.5">
          <span
            v-for="topic in COVERED_TOPICS"
            :key="topic"
            class="inline-flex h-6.5 items-center rounded-full bg-secondary px-2.5 text-xs text-foreground-secondary">
            {{ topic }}
          </span>
        </div>
      </div>
      <p
        v-if="context.status === 'failed'"
        class="text-xs text-status-error-strong">
        The last generation failed. Try again, or remove a source Episto AI could not read.
      </p>
    </div>

    <div
      v-if="footer && content && !generating"
      class="mt-auto shrink-0 border-t px-4 py-3 text-xs"
      :class="context.status === 'failed' ? 'text-status-error-strong' : 'text-muted-foreground'">
      {{ footer }}
    </div>

    <ConfirmDialog
      v-model:open="confirmOpen"
      title="Regenerate the context?"
      description="Episto AI writes a new context from the questionnaire and the sources. It replaces the current text, your edits included."
      confirm-label="Regenerate"
      variant="default"
      @confirm="emit('generate')" />
  </section>
</template>
